/**
 * Mayvenn — Package Protection opt-in (3 Oct 2026).
 *
 * Works with snippets/mv-cart-protection.liquid. Three jobs:
 *   1. The checkbox adds or removes the fee variant for the cart's tier.
 *   2. After any cart change, swap the fee to the right tier if the cart value moved across a limit.
 *   3. If only the fee is left in the cart, remove it.
 *
 * All cart writes go through /cart/update.js and Horizon's CartLinesUpdateEvent with a deferred
 * promise, so the drawer, cart page and header bubble re-render the same way as any other change.
 * The fee is never added unless the shopper checks the box.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const HANDLE = 'package-protection';
const ROOT = '[data-mv-pp]';
const TOGGLE = '[data-mv-pp-toggle]';
const EVENT = StandardEvents?.cartLinesUpdate ?? 'shopify:cart:lines-update';

let busy = false;
let queued = false;
let dispatching = false;

const rootUrl = () => window.Shopify?.routes?.root ?? '/';

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);
}

async function fetchCart() {
  const res = await fetch(`${rootUrl()}cart.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!res.ok) throw new Error(`Cart fetch failed: ${res.status}`);
  return res.json();
}

/** Tier limits (in cents) and variant ids, rendered by the snippet. A limit of 0 means no upper limit. */
function readTiers() {
  const el = document.querySelector(ROOT);
  const raw = el instanceof HTMLElement ? el.dataset.tiers : '';
  if (!raw) return null;
  return raw.split('|').map((pair) => {
    const [limit, id] = pair.split(':');
    return { limit: Number(limit), id: Number(id) };
  });
}

function targetFor(total, tiers) {
  for (const tier of tiers) {
    if (tier.limit === 0 || total < tier.limit) return tier.id;
  }
  return tiers[tiers.length - 1].id;
}

function split(cart) {
  const fee = cart.items.filter((item) => item.handle === HANDLE);
  const hair = cart.items.filter((item) => item.handle !== HANDLE);
  const feeTotal = fee.reduce((sum, item) => sum + item.final_line_price, 0);
  return { fee, hair, hairTotal: cart.total_price - feeTotal };
}

function syncToggles(on) {
  document.querySelectorAll(TOGGLE).forEach((input) => {
    if (input instanceof HTMLInputElement) input.checked = on;
  });
}

function track(action, variantId) {
  const params = { experience: 'package_protection', action, variant_id: variantId };
  if (typeof window.mvTrack === 'function') {
    window.mvTrack(`mv_package_protection_${action}`, params);
  } else {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: `mv_package_protection_${action}` }, params));
  }
}

/**
 * @param {Record<string, number>} updates - variant id -> quantity
 * @param {'add' | 'remove' | 'update'} action
 * @param {Array<object>} lines
 */
async function apply(updates, action, lines) {
  const deferred = CartLinesUpdateEvent.createPromise();
  dispatching = true;
  try {
    document.body.dispatchEvent(
      new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise })
    );
  } finally {
    dispatching = false;
  }

  try {
    const res = await fetch(`${rootUrl()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        updates,
        sections: cartSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Cart update failed');

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
      detail: {
        items: data.items,
        sections: data.sections,
        itemCount: data.item_count,
        source: 'mv-package-protection',
        didError: false,
      },
    });
    return data;
  } catch (err) {
    deferred.reject(err);
    throw err;
  }
}

/** Keep the fee line honest after any cart change. Never adds the fee on its own. */
async function reconcile() {
  if (busy) {
    queued = true;
    return;
  }
  busy = true;
  try {
    const cart = await fetchCart();
    const { fee, hair, hairTotal } = split(cart);
    syncToggles(fee.length > 0);
    if (!fee.length) return;

    /** @type {Record<string, number>} */
    const updates = {};

    if (!hair.length) {
      fee.forEach((item) => (updates[item.variant_id] = 0));
      await apply(updates, 'remove', fee.map((item) => ({ id: item.key, quantity: 0 })));
      syncToggles(false);
      return;
    }

    const tiers = readTiers();
    if (!tiers) return;
    const target = targetFor(hairTotal, tiers);
    const correct = fee.length === 1 && fee[0].variant_id === target && fee[0].quantity === 1;
    if (correct) return;

    fee.forEach((item) => {
      if (item.variant_id !== target) updates[item.variant_id] = 0;
    });
    updates[target] = 1;
    await apply(updates, 'update', fee.map((item) => ({ id: item.key, quantity: item.variant_id === target ? 1 : 0 })));
  } catch (err) {
    console.error('[mv-cart-protection] reconcile failed', err);
  } finally {
    busy = false;
    if (queued) {
      queued = false;
      reconcile();
    }
  }
}

/** @param {HTMLInputElement} input */
async function toggle(input) {
  const card = input.closest(ROOT);
  const error = card?.querySelector('.mv-pp__error');
  const wanted = input.checked;

  if (busy) {
    input.checked = !wanted;
    return;
  }
  busy = true;
  card?.setAttribute('aria-busy', 'true');
  if (error instanceof HTMLElement) error.hidden = true;

  try {
    const cart = await fetchCart();
    const { fee, hair, hairTotal } = split(cart);
    /** @type {Record<string, number>} */
    const updates = {};

    if (wanted) {
      const tiers = readTiers();
      if (!hair.length || !tiers) {
        input.checked = false;
        return;
      }
      const target = targetFor(hairTotal, tiers);
      fee.forEach((item) => {
        if (item.variant_id !== target) updates[item.variant_id] = 0;
      });
      updates[target] = 1;
      await apply(updates, 'add', [{ merchandiseId: String(target), quantity: 1 }]);
      track('add', target);
    } else {
      if (!fee.length) return;
      fee.forEach((item) => (updates[item.variant_id] = 0));
      await apply(updates, 'remove', fee.map((item) => ({ id: item.key, quantity: 0 })));
      track('remove', fee[0].variant_id);
    }
    syncToggles(wanted);
  } catch (err) {
    console.error('[mv-cart-protection] toggle failed', err);
    input.checked = !wanted;
    if (error instanceof HTMLElement) {
      error.textContent = 'Could not update Package Protection. Please try again.';
      error.hidden = false;
    }
  } finally {
    busy = false;
    card?.removeAttribute('aria-busy');
    document.querySelectorAll(`${ROOT}[aria-busy]`).forEach((el) => el.removeAttribute('aria-busy'));
    if (queued) {
      queued = false;
      reconcile();
    }
  }
}

if (!window.__mvCartProtection) {
  window.__mvCartProtection = true;

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches(TOGGLE)) return;
    toggle(input);
  });

  document.addEventListener(EVENT, (event) => {
    if (dispatching) return;
    const promise = /** @type {any} */ (event).promise;
    if (promise && typeof promise.then === 'function') {
      promise.then(
        () => reconcile(),
        () => {}
      );
    } else {
      reconcile();
    }
  });

  // A returning visitor whose cart already holds the fee: check the tier once on load.
  if (document.querySelector(`${ROOT}[data-has-fee='true']`)) reconcile();
}
